import React from 'react';
import styled from 'styled-components';

import { Section } from './Section';
import { ItemOverlay } from './ItemOverlay';

import {
  type UseMultiselectReturn,
  type SelectableItem,
} from './useMultiselect';
import type { MultiselectPickerItemComponentProps } from './types';

type MultiselectPickerProps<TItem extends SelectableItem> = {
  'data-testid'?: string;
  selectableItems:
    | TItem[]
    | {
        [key: string]: TItem[];
      };
  multiselect: UseMultiselectReturn;
  keyExtractor?: (item: TItem) => string | number;
  ItemComponent: React.ComponentType<
    MultiselectPickerItemComponentProps<TItem>
  >;
  onItemOverlayClicked?: (item: TItem) => void;
};

const Sections = styled.div`
  padding: 0 0 var(--cc_size_spacing_xl);
  display: grid;
  gap: var(--cc_size_spacing_l);
`;

const FlatList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  column-gap: var(--cc_size_spacing_s);
  row-gap: var(--cc_size_spacing_l);
`;

export const MultiselectPicker = <TItem extends SelectableItem>({
  multiselect,
  selectableItems,
  keyExtractor = (item) => item.id,
  ItemComponent,
  onItemOverlayClicked,
  ...props
}: MultiselectPickerProps<TItem>) => {
  const { 'data-testid': dataTestId } = props;
  if (Array.isArray(selectableItems)) {
    return (
      <FlatList data-testid={dataTestId}>
        {selectableItems.map((item, index) => (
          <ItemOverlay
            label={`item ${index + 1}`}
            key={keyExtractor(item)}
            item={item}
            multiselect={multiselect}
            ItemComponent={ItemComponent}
            onItemOverlayClicked={onItemOverlayClicked}
          />
        ))}
      </FlatList>
    );
  }

  return (
    <Sections data-testid={dataTestId}>
      {Object.keys(selectableItems).map((key) => (
        <Section
          key={key}
          keyExtractor={keyExtractor}
          selectableItems={selectableItems[key]}
          multiselect={multiselect}
          title={key}
          ItemComponent={ItemComponent}
          onItemOverlayClicked={onItemOverlayClicked}
        />
      ))}
    </Sections>
  );
};
